import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { deviceLogin, deviceForceLogin, getDeviceByCode } from '../services/devices';
import './DeviceLogin.css';

function DeviceLogin() {
  const [deviceCode, setDeviceCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const navigate = useNavigate();

  // Generate a unique session ID for this browser session
  const [sessionId] = useState(() => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  });

  useEffect(() => {
    // Check if company login is required
    const savedCompanyId = localStorage.getItem('companyId');
    const savedCompanyName = localStorage.getItem('companyName');

    if (!savedCompanyId) {
      // Redirect to company login if not logged in
      navigate('/company-login');
      return;
    }

    setCompanyInfo({
      id: savedCompanyId,
      name: savedCompanyName
    });

    // Check if already logged in
    const savedDeviceId = localStorage.getItem('deviceId');
    const savedSessionId = localStorage.getItem('sessionId');

    if (savedDeviceId && savedSessionId === sessionId) {
      navigate(`/device/${savedDeviceId}`);
    }
  }, [navigate, sessionId]);

  const handleLogin = async (forceLogin = false) => {
    if (!deviceCode.trim()) {
      setError('請輸入設備代碼');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // First verify device belongs to this company
      const device = await getDeviceByCode(deviceCode);

      if (!device) {
        setError('設備代碼不存在');
        setLoading(false);
        return;
      }

      if (device.companyId !== companyInfo.id) {
        setError('此設備不屬於您的公司');
        setLoading(false);
        return;
      }

      let result;
      if (forceLogin) {
        result = await deviceForceLogin(deviceCode, sessionId);
      } else {
        result = await deviceLogin(deviceCode, sessionId);
      }

      if (result.success) {
        // Save device info to localStorage
        localStorage.setItem('deviceId', result.device.id);
        localStorage.setItem('deviceCode', deviceCode);
        localStorage.setItem('sessionId', sessionId);

        // Navigate to device display page
        navigate(`/device/${result.device.id}`);
      } else if (result.needKickout) {
        // Ask user if they want to force login
        const confirmed = window.confirm(
          '此設備已在其他地方登入。是否要強制登入？（將會登出其他會話）'
        );

        if (confirmed) {
          await handleLogin(true);
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleLogin(false);
  };

  const handleBackToCompany = () => {
    localStorage.removeItem('companyId');
    localStorage.removeItem('companyCode');
    localStorage.removeItem('companyName');
    navigate('/company-login');
  };

  if (!companyInfo) {
    return null; // Will redirect to company login
  }

  return (
    <div className="device-login">
      <div className="device-login-container">
        <div className="company-badge">
          <span className="company-label">公司:</span>
          <span className="company-name">{companyInfo.name}</span>
          <button
            className="btn-change-company"
            onClick={handleBackToCompany}
            title="更換公司"
          >
            ↻
          </button>
        </div>

        <div className="device-login-header">
          <h1>設備登入</h1>
          <p>請輸入您的設備代碼</p>
        </div>

        <form className="device-login-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <input
              type="text"
              className="device-code-input"
              placeholder="設備代碼"
              value={deviceCode}
              onChange={(e) => setDeviceCode(e.target.value)}
              disabled={loading}
              autoFocus
            />
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary btn-large"
            disabled={loading}
          >
            {loading ? '登入中...' : '登入'}
          </button>
        </form>

        <div className="device-login-footer">
          <p className="help-text">
            如果您還沒有設備代碼，請聯繫管理員
          </p>
        </div>
      </div>
    </div>
  );
}

export default DeviceLogin;
